import type React from 'react'
import { Button } from '@components/ui/button'
import type { FontFamily } from '@/api/types/font-family'
import { keepTextEditFocus } from '../handlers/keep-text-edit-focus'
import { SectionHeading } from './section-heading'

interface FontFamilySectionProps {
  isUsedCatalogLoading: boolean
  fontLoadAnnouncement: string
  fontSelectionAnnouncement: string
  recentFontFamilies: FontFamily[]
  failedFontFamilyNames: ReadonlySet<string>
  disabled: boolean
  hasTextSelectionTarget: boolean
  fontFamilyIdValue: string
  onApplyRecent: (fontFamilyId: number) => void
  /** The family picker. */
  children: React.ReactNode
}

/** Font Family: screen-reader live regions, recently applied typefaces, and the family picker. */
export function FontFamilySection({
  isUsedCatalogLoading,
  fontLoadAnnouncement,
  fontSelectionAnnouncement,
  recentFontFamilies,
  failedFontFamilyNames,
  disabled,
  hasTextSelectionTarget,
  fontFamilyIdValue,
  onApplyRecent,
  children,
}: FontFamilySectionProps) {
  return (
    <div className="space-y-3" aria-busy={isUsedCatalogLoading}>
      {/* Visually hidden live regions — announce font loading/error state changes to AT */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {fontLoadAnnouncement}
      </div>
      {/* Separate live region for selection state (mixed families / font changes) so it
          doesn't clobber (or get clobbered by) the loading announcement above. */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {fontSelectionAnnouncement}
      </div>
      <SectionHeading>Font Family</SectionHeading>
      {recentFontFamilies.length > 0 && (
        <div className="mt-2">
          <div
            className="flex gap-2 mt-1 flex-wrap"
            onPointerDown={keepTextEditFocus}
            aria-label="Recently used typefaces"
          >
            {recentFontFamilies.map((fam) => {
              const isFontFailed = failedFontFamilyNames.has(fam.familyName)

              return (
                <Button
                  key={fam.fontFamilyId}
                  variant="ghost"
                  size="xs"
                  disabled={disabled || !hasTextSelectionTarget || isFontFailed}
                  aria-label={`Apply ${fam.displayName} font family`}
                  aria-pressed={String(fam.fontFamilyId) === fontFamilyIdValue}
                  onClick={() => onApplyRecent(fam.fontFamilyId)}
                  className="font-medium text-foreground whitespace-nowrap min-w-[3.75rem] sm:min-w-[4.375rem]"
                  style={{ fontFamily: `"${fam.familyName}", sans-serif` }}
                >
                  {fam.displayName}
                </Button>
              )
            })}
          </div>
        </div>
      )}

      {children}
    </div>
  )
}
